import { UserMinus, X } from 'lucide-react';
import { MEMBER_GENDER_LABELS, type MemberGender } from '@bm/contracts';
import { useLockBodyScroll } from '../../lib/use-lock-body-scroll';

export interface SelectablePlayer {
  memberId: string;
  name: string;
  teamLabel?: string | null;
  gender: MemberGender | null;
  appearanceCount: number;
  eligible: boolean;
  ineligibleReason?: string;
}

/**
 * Universeller Spieler-Auswahl-Dialog für einen Slot — der Kernweg für die
 * Zuordnung auf JEDEM Gerät (Mobile primär, Desktop als Alternative zu
 * Drag&Drop, siehe §6/§21). Als Bottom-Sheet auf schmalen Bildschirmen
 * (`items-end`), zentriertes Modal ab `sm:`. Unpassende Spieler bleiben
 * sichtbar, aber deaktiviert mit Begründung (§9), statt einfach zu
 * verschwinden — das macht die Regel nachvollziehbar statt überraschend.
 */
export function PlayerSelectDrawer({
  title,
  subtitle,
  players,
  currentMemberId,
  onSelect,
  onClear,
  onClose,
}: {
  title: string;
  subtitle: string;
  players: SelectablePlayer[];
  currentMemberId: string | null;
  onSelect: (memberId: string) => void;
  onClear?: () => void;
  onClose: () => void;
}) {
  useLockBodyScroll();
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-w-md sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-800">{title}</h2>
            <p className="text-xs text-slate-500">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {currentMemberId && onClear && (
            <button
              type="button"
              onClick={onClear}
              className="mb-2 flex w-full items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2.5 text-left text-sm text-slate-500 hover:border-red-300 hover:text-red-600"
            >
              <UserMinus size={16} />
              Position freilassen
            </button>
          )}
          {players.length === 0 && (
            <p className="px-2 py-6 text-center text-sm text-slate-400">
              Keine zugesagten Spieler verfügbar.
            </p>
          )}
          <ul className="space-y-1.5">
            {players.map((p) => {
              const isCurrent = p.memberId === currentMemberId;
              return (
                <li key={p.memberId}>
                  <button
                    type="button"
                    disabled={!p.eligible && !isCurrent}
                    onClick={() => onSelect(p.memberId)}
                    title={!p.eligible && !isCurrent ? p.ineligibleReason : undefined}
                    className={`flex w-full min-h-[44px] items-center justify-between gap-2 rounded-md border px-3 py-2.5 text-left text-sm transition ${
                      isCurrent
                        ? 'border-brand-accent bg-brand-accent/5 font-medium text-brand-accent'
                        : p.eligible
                          ? 'border-slate-200 text-slate-700 hover:border-brand-accent hover:bg-brand-accent/5'
                          : 'cursor-not-allowed border-slate-100 text-slate-300'
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{p.name}</span>
                      <span className="block truncate text-xs font-normal opacity-70">
                        {[p.gender ? MEMBER_GENDER_LABELS[p.gender] : null, p.teamLabel, `${p.appearanceCount} Einsätze`]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                      {!p.eligible && !isCurrent && p.ineligibleReason && (
                        <span className="mt-0.5 block text-xs font-normal text-amber-600">{p.ineligibleReason}</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
